import React from 'react';
import type { TpObjection, TpObjectionLikelihood } from '../../patient-treatment/types';
import { Icon } from '../Icon/Icon';
import { PatientProfileTabSectionHeader } from './PatientProfileTabSectionHeader';

const LIKELIHOOD: Record<
  TpObjectionLikelihood,
  { dot: string; label: string; bg: string; fg: string }
> = {
  high: {
    dot: 'var(--terracotta)',
    label: 'Likely',
    bg: 'var(--terracotta-tint)',
    fg: 'var(--terracotta)',
  },
  medium: {
    dot: 'var(--gold)',
    label: 'Possible',
    bg: 'var(--gold-tint)',
    fg: 'var(--gold)',
  },
};

function ObjectionCard({ obj }: { obj: TpObjection }) {
  const lk = LIKELIHOOD[obj.likelihood];

  return (
    <div className="tc-card flex flex-col px-[18px] py-4">
      <div className="mb-2.5">
        <span
          className="inline-flex items-center gap-1.5 rounded-[var(--r-pill)] px-2.5 py-0.5 text-[10px] font-bold tracking-[0.02em]"
          style={{ background: lk.bg, color: lk.fg }}
        >
          <span className="h-[5px] w-[5px] rounded-full" style={{ background: lk.dot }} />
          {lk.label}
        </span>
      </div>
      <h4 className="mb-1 text-[15px] font-semibold leading-[1.35] tracking-[-0.01em] text-[var(--ink-0)]">
        {obj.trigger}
      </h4>
      <div className="mb-3 text-[11.5px] leading-[1.45] text-[var(--ink-3)]">{obj.when}</div>
      <div
        className="mt-auto rounded-[var(--r-md)] border px-3.5 py-3"
        style={{
          background: 'var(--gold-tint)',
          borderColor: 'color-mix(in srgb, var(--gold) 28%, transparent)',
        }}
      >
        <div className="mb-1.5 flex items-center gap-1.5">
          <Icon kind="book" size={10} stroke={1.9} style={{ color: 'var(--gold)' }} />
          <span className="tc-eyebrow text-[var(--gold)]">Practice playbook</span>
        </div>
        <p className="m-0 text-[12.5px] leading-[1.6] tracking-[-0.005em] text-[var(--ink-1)]">
          {obj.practiceApproach}
        </p>
      </div>
    </div>
  );
}

export interface PatientTreatmentObjectionsProps {
  objections: TpObjection[];
}

export function PatientTreatmentObjections({ objections }: PatientTreatmentObjectionsProps) {
  return (
    <section className="tc-rise tc-d4 mb-[30px]">
      <PatientProfileTabSectionHeader
        eyebrow="Anticipate"
        title="What they'll likely push back on"
        meta={`${objections.length} from playbook`}
        icon="shield"
        accent="var(--ink-3)"
      />
      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}
      >
        {objections.map((obj) => (
          <ObjectionCard key={obj.id} obj={obj} />
        ))}
      </div>
    </section>
  );
}
